import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import IkiSymbol from './IkiSymbol';

/**
 * Reusable Audio Recorder Component
 * 
 * Features:
 * - MediaRecorder API with feature detection
 * - Graceful fallback for unsupported browsers (iOS Safari)
 * - Visual recording feedback with waveform animation
 * - Timer display
 * - Auto-stop at max duration
 * - Upload status tracking
 * 
 * @param {Function} onRecordingComplete - Callback when recording is ready (receives blob)
 * @param {Number} maxDuration - Maximum recording duration in seconds (default: 60)
 * @param {Boolean} autoUpload - If true, automatically triggers upload after recording
 * @param {Function} onUploadComplete - Callback after successful upload (receives response)
 * @param {String} uploadEndpoint - API endpoint for upload
 * @param {Object} uploadData - Additional data to send with upload
 */
const AudioRecorder = ({
  onRecordingComplete,
  maxDuration = 60,
  autoUpload = false,
  onUploadComplete,
  uploadEndpoint,
  uploadData = {},
  hideSaveButton = false,
  autoStart = false
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [waveformBars, setWaveformBars] = useState(Array(30).fill(20));
  const [uploadStatus, setUploadStatus] = useState(null); // 'uploading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [isSupported, setIsSupported] = useState(true);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const animationRef = useRef(null);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  // Check MediaRecorder support on mount
  useEffect(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setIsSupported(false);
      setErrorMessage('Your browser does not support audio recording. Please use a modern browser like Chrome, Firefox, or Safari on desktop.');
    } else if (!window.MediaRecorder) {
      setIsSupported(false);
      setErrorMessage('MediaRecorder API is not supported in your browser. Please update your browser or try a different one.');
    } else if (autoStart) {
      // Auto-start recording immediately on mount
      startRecording();
    }
  }, []); // eslint-disable-line

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
      // Stop active MediaRecorder before killing the stream
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try { mediaRecorderRef.current.stop(); } catch (_) {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Animate waveform while recording
  useEffect(() => {
    if (isRecording) {
      animateWaveform();
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Close AudioContext when recording stops
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
        analyserRef.current = null;
      }
      // Reset to flat bars when idle
      setWaveformBars(Array(30).fill(20));
    }
  }, [isRecording]); // eslint-disable-line

  const animateWaveform = () => {
    if (analyserRef.current) {
      // Real microphone level via Web Audio API
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const barCount = 30;
      const step = Math.floor(dataArray.length / barCount);
      const bars = Array.from({ length: barCount }, (_, i) => {
        const raw = dataArray[i * step] || 0;
        return Math.max(8, (raw / 255) * 80 + 8);
      });
      setWaveformBars(bars);
    } else {
      // Fallback: random waveform when Web Audio API unavailable
      setWaveformBars(prev => prev.map(() => Math.random() * 80 + 20));
    }
    animationRef.current = requestAnimationFrame(animateWaveform);
  };

  const startRecording = async () => {
    if (!isSupported) {
      toast.error(errorMessage);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Wire up Web Audio API for real-time level visualisation
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          const audioContext = new AudioCtx();
          audioContextRef.current = audioContext;
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyserRef.current = analyser;
          const source = audioContext.createMediaStreamSource(stream);
          source.connect(analyser);
        }
      } catch (e) {
        console.warn('Web Audio API unavailable, falling back to random waveform:', e);
      }

      // Try to use the best audio format available
      // Prioritize formats that work across all platforms
      let mimeType = 'audio/webm'; // default
      
      // Check supported formats in order of preference
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4'; // Best for iOS
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus'; // Best for Chrome/Firefox
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }

      console.log('Using audio format:', mimeType);

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType
      });
      
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        
        setAudioBlob(blob);
        setAudioUrl(url);
        
        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }
        
        // Call parent callback
        if (onRecordingComplete) {
          onRecordingComplete(blob);
        }

        // Auto-upload if enabled
        if (autoUpload && uploadEndpoint) {
          uploadRecording(blob);
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        toast.error('Recording failed. Please try again.');
        stopRecording();
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setUploadStatus(null);
      setErrorMessage('');

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          if (newTime >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return newTime;
        });
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      
      if (error.name === 'NotAllowedError') {
        setErrorMessage('Microphone access denied. Please allow microphone access in your browser settings.');
        toast.error('Microphone access denied');
      } else if (error.name === 'NotFoundError') {
        setErrorMessage('No microphone found. Please connect a microphone and try again.');
        toast.error('No microphone found');
      } else {
        setErrorMessage('Failed to access microphone. Please check your browser settings.');
        toast.error('Failed to start recording');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    
    setIsRecording(false);
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const uploadRecording = async (blob) => {
    if (!uploadEndpoint) {
      console.error('No upload endpoint provided');
      return;
    }

    try {
      setUploadStatus('uploading');

      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      
      // Add additional upload data
      Object.keys(uploadData).forEach(key => {
        formData.append(key, uploadData[key]);
      });

      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      setUploadStatus('success');
      toast.success('Recording saved successfully!');
      
      if (onUploadComplete) {
        onUploadComplete(data);
      }

      // Reset after success
      setTimeout(() => {
        resetRecorder();
      }, 2000);

    } catch (error) {
      console.error('Error uploading recording:', error);
      setUploadStatus('error');
      setErrorMessage('Failed to upload recording. Please try again.');
      toast.error('Failed to save recording');
    }
  };

  const resetRecorder = () => {
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setRecordingTime(0);
    setUploadStatus(null);
    setErrorMessage('');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // If browser not supported, show error message
  if (!isSupported) {
    return (
      <div style={{
        padding: '24px',
        background: 'rgba(244, 67, 54, 0.1)',
        borderRadius: '12px',
        border: '1px solid rgba(244, 67, 54, 0.3)',
        textAlign: 'center'
      }}>
        <IkiSymbol name="alert" size="lg" />
        <p style={{ color: '#D9534F', marginTop: '12px', fontSize: '0.95rem' }}>
          {errorMessage}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px',
      background: 'rgba(255, 255, 255, 0.6)',
      borderRadius: '16px',
      border: '1px solid rgba(30, 107, 104, 0.2)'
    }}>
      {/* Waveform Visualization */}
      {isRecording && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          height: '80px',
          gap: '3px',
          marginBottom: '16px'
        }}>
          {waveformBars.map((height, i) => (
            <div
              key={i}
              style={{
                width: '6px',
                height: `${height}px`,
                background: 'linear-gradient(to top, #1E6B68, #B8D3CF)',
                borderRadius: '3px',
                transition: 'height 0.1s ease'
              }}
            />
          ))}
        </div>
      )}

      {/* Timer */}
      {(isRecording || audioBlob) && (
        <div style={{
          textAlign: 'center',
          fontSize: '1.5rem',
          color: '#1E6B68',
          marginBottom: '16px',
          fontFamily: 'monospace',
          fontWeight: '600'
        }}>
          {formatTime(recordingTime)}
        </div>
      )}

      {/* Audio Playback */}
      {audioUrl && !isRecording && (
        <div style={{ marginBottom: '16px', textAlign: 'center' }}>
          <audio 
            controls 
            src={audioUrl}
            style={{
              width: '100%',
              maxWidth: '400px',
              height: '40px'
            }}
          />
        </div>
      )}

      {/* Recording Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        {!isRecording && !audioBlob && (
          <button
            onClick={startRecording}
            style={{
              padding: '12px 28px',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff',
              border: 'none',
              borderRadius: '24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(30, 107, 104, 0.3)',
              transition: 'transform 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          >
            <IkiSymbol name="mic" size="sm" />
            Start Recording
          </button>
        )}

        {isRecording && (
          <button
            onClick={stopRecording}
            style={{
              padding: '12px 28px',
              background: '#D9534F',
              color: '#fff',
              border: 'none',
              borderRadius: '24px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(217, 83, 79, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <IkiSymbol name="stop" size="sm" />
            Stop Recording
          </button>
        )}

        {audioBlob && !isRecording && !autoUpload && (
          <>
            <button
              onClick={resetRecorder}
              style={{
                padding: '12px 24px',
                background: 'rgba(184, 211, 207, 0.5)',
                color: '#1E6B68',
                border: '2px solid #B8D3CF',
                borderRadius: '24px',
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Re-record
            </button>
            {!hideSaveButton && (
              <button
                onClick={() => uploadRecording(audioBlob)}
                disabled={uploadStatus === 'uploading'}
                style={{
                  padding: '12px 28px',
                  background: uploadStatus === 'uploading'
                    ? '#ccc'
                    : 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '24px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: uploadStatus === 'uploading' ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 12px rgba(30, 107, 104, 0.3)'
                }}
              >
                {uploadStatus === 'uploading' ? 'Uploading...' : 'Save Recording'}
              </button>
            )}
          </>
        )}
      </div>

      {/* Upload Status */}
      {uploadStatus === 'success' && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: 'rgba(76, 175, 80, 0.1)',
          border: '1px solid rgba(76, 175, 80, 0.3)',
          borderRadius: '8px',
          color: '#4CAF50',
          textAlign: 'center',
          fontSize: '0.95rem'
        }}>
          ✓ Recording saved successfully!
        </div>
      )}

      {uploadStatus === 'error' && errorMessage && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: 'rgba(244, 67, 54, 0.1)',
          border: '1px solid rgba(244, 67, 54, 0.3)',
          borderRadius: '8px',
          color: '#D9534F',
          textAlign: 'center',
          fontSize: '0.95rem'
        }}>
          {errorMessage}
        </div>
      )}

      {/* Recording Info */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: 'rgba(184, 211, 207, 0.15)',
        borderRadius: '8px',
        fontSize: '0.85rem',
        color: '#666',
        textAlign: 'center'
      }}>
        Max duration: {formatTime(maxDuration)} • Clear, quiet environment recommended
      </div>
    </div>
  );
};

export default AudioRecorder;
